import clsx, { ClassValue } from 'clsx';
import { Easing, motion, useInView } from 'framer-motion';
import { ReactNode, useRef } from 'react';

const directionVariants: Record<
  string,
  {
    initial: { opacity: number; x?: number; y?: number };
    animate: { opacity: number; x?: number; y?: number };
  }
> = {
  up: {
    initial: { opacity: 0, y: -40 },
    animate: { opacity: 1, y: 0 },
  },
  down: {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
  },
  left: {
    initial: { opacity: 0, x: -40 },
    animate: { opacity: 1, x: 0 },
  },
  right: {
    initial: { opacity: 0, x: 40 },
    animate: { opacity: 1, x: 0 },
  },
  none: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
  },
};

interface FadeInProps {
  children: ReactNode;
  className?: string | ClassValue[];
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  duration?: number;
  ease?: Easing | Easing[];
  onScroll?: boolean;
  inViewAmount?: number;
}

export const FadeIn = ({
  children,
  className = '',
  delay = 0,
  direction = 'down',
  duration = 0.5,
  ease = 'easeOut',
  onScroll = false,
  inViewAmount = 0.3,
}: FadeInProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: inViewAmount });
  const variants = directionVariants[direction];

  const animate = onScroll
    ? isInView
      ? 'animate'
      : 'initial'
    : variants.animate;

  return (
    <motion.div
      ref={onScroll ? ref : undefined}
      initial={variants.initial}
      animate={animate}
      variants={variants}
      transition={{
        duration,
        delay,
        ease,
      }}
      className={clsx(className)}
    >
      {children}
    </motion.div>
  );
};
